import { Injectable } from '@angular/core';
import { VimCoreBaseConfig } from '@skyeng/libs/vimbox-core/base';
import { fromEvent, Observable, of, race, throwError } from 'rxjs';
import { mapTo, shareReplay, switchMapTo } from 'rxjs/operators';
import { VimCoreWidgetsType } from '../interface';

interface Widget {
  url: string;
  loading: Observable<void> | undefined;
}

@Injectable({ providedIn: 'root' })
export class VimCoreWidgetsService {
  private widgetsBundles: Record<VimCoreWidgetsType, Widget> = {
    main: {
      url: `//widgets-host.${this.vimCoreBaseConfig.rootDomain}/loader.js`,
      loading: undefined,
    },
    vimboxWidgets: {
      url: `//widgets.${this.vimCoreBaseConfig.rootDomain}/widgets.bundle.js`,
      loading: undefined,
    },
    motivationWidgets: {
      url: `//widgets-motivation.${this.vimCoreBaseConfig.rootDomain}/loader.js`,
      loading: undefined,
    },
  };

  constructor(private vimCoreBaseConfig: VimCoreBaseConfig) {}

  load(type: VimCoreWidgetsType): Observable<void> {
    const widget = this.widgetsBundles[type];

    if (!widget) {
      throw new Error(`VimCoreWidgetsService: can't load unknown '${type}' widgets type`);
    }

    if (widget.loading) {
      return widget.loading;
    }

    const nonCachedwidgetsUrl = `${widget.url}?${Date.now()}`;

    // eslint-disable-next-line functional/immutable-data
    this.widgetsBundles[type].loading = this.widgetScriptAlreadyLoaded(widget.url)
      ? of(undefined)
      : this.loadScript(type, nonCachedwidgetsUrl);

    return this.widgetsBundles[type].loading!;
  }

  loadScript(name: string, url: string): Observable<void> {
    const scriptId = `vim-core-script-${name}`;

    // script is already loaded
    if (document.querySelector(`#${scriptId}`)) {
      return of(undefined).pipe(shareReplay({ refCount: false, bufferSize: 1 }));
    }

    const scriptElement = document.createElement('script');
    /* eslint-disable functional/immutable-data */
    scriptElement.src = url;
    scriptElement.async = true;
    scriptElement.id = scriptId;
    /* eslint-enable functional/immutable-data */

    const loaded$ = fromEvent(scriptElement, 'load').pipe(mapTo(undefined));
    const error$ = fromEvent(scriptElement, 'error').pipe(
      switchMapTo(throwError(new Error(`VimCoreWidgetsService: cannot load '${name}' widgets from '${url}' url`))),
    );

    document.body.appendChild(scriptElement);

    return race(loaded$, error$).pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  private widgetScriptAlreadyLoaded(widgetSrc: string): boolean {
    const alreadyLoadedScripts = Array.from(document.querySelectorAll('script')).map(script => script.src);

    return alreadyLoadedScripts.some(src => src.includes(widgetSrc));
  }
}
